import React, { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import SearchBar from "../../ui/SearchBar";
import { PageLoadingView } from "../../common/PageLoadingView";
import { PageErrorView } from "../../common/PageErrorView";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { useSpecialProducts } from "../../../hooks/useSpecialProducts";
import { useUser } from "../../../context/UserInfoProvider";
import { DataManagementSidebar } from "./subcomponents/DataManagementSidebar";
import { SpecialProductItem } from "../../../data/specialProductsData";

interface ValidationErrors {
  name?: string;
  description?: string;
  amount?: string;
  imageUrl?: string;
}

const SpecialProductsData: React.FC = () => {
  const { hotelId } = useUser();

  // Use the updated hook instead of local state management
  const {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    setSearchTerm,
    refresh,
  } = useSpecialProducts({ hotelId: hotelId || "" });

  // UI-specific state (keep in component)
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const validateProduct = (
    product: Partial<SpecialProductItem>
  ): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!product.name?.trim()) {
      errors.name = "Name is required";
    }

    if (!product.description?.trim()) {
      errors.description = "Description is required";
    }

    if (product.amount === undefined || product.amount < 0) {
      errors.amount = "Amount must be a non-negative number";
    }

    return errors;
  };

  const handleItemSelect = (item: SpecialProductItem) => {
    // If we're creating a new item and user clicks an existing one, warn them
    if (isCreating) {
      setConfirmationDialog({
        isOpen: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes for a new special product. Do you want to discard these changes and edit the selected product?",
        confirmText: "Discard Changes",
        cancelText: "Cancel",
        onConfirm: () => {
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
          setSelectedItem(item);
          setValidationErrors({});
          setUpdateError(null);
          setIsCreating(false);
        },
      });
      return;
    }

    setSelectedItem(item);
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(false);
  };

  const handleAddItem = () => {
    setSelectedItem({
      productId: "",
      hotelId: hotelId || "",
      name: "",
      description: "",
      amount: 0,
      imageUrl: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setValidationErrors({});
    setUpdateError(null);
    setIsCreating(true);
  };

  const handleSave = async () => {
    if (!selectedItem || isUpdating) return;

    // Validate the form
    const errors = validateProduct(selectedItem);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setValidationErrors({});

      if (isCreating) {
        // Create new special product using hook
        await createItem({
          hotelId: hotelId || "",
          name: selectedItem.name || "",
          description: selectedItem.description || "",
          amount: selectedItem.amount || 0,
          imageUrl: selectedItem.imageUrl || "",
        });
        setIsCreating(false);
        // Refresh to ensure UI updates
        await refresh();
      } else {
        // Update existing special product using hook
        const updates: Partial<SpecialProductItem> = {};

        if (selectedItem.name !== undefined) updates.name = selectedItem.name;
        if (selectedItem.description !== undefined)
          updates.description = selectedItem.description;
        if (selectedItem.amount !== undefined)
          updates.amount = selectedItem.amount;
        if (selectedItem.imageUrl !== undefined)
          updates.imageUrl = selectedItem.imageUrl;

        await updateItem(selectedItem.productId, updates);
        // Refresh to ensure UI updates
        await refresh();
      }

      // Close sidebar
      setSelectedItem(null);
    } catch (error) {
      console.error("Error saving product:", error);
      setUpdateError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while saving the product"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: "Delete Product",
      message: "Are you sure you want to delete this product?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));

        try {
          setIsUpdating(true);
          await deleteItem(itemId);

          if (selectedItem?.productId === itemId) {
            setSelectedItem(null);
          }
        } catch (error) {
          console.error("Error deleting product:", error);
          setUpdateError(
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while deleting the product"
          );
        } finally {
          setIsUpdating(false);
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if ([10, 20, 50].includes(newLimit)) {
      setLimit(newLimit);
      setPage(1);
    }
  };

  const filteredItems = items;

  if (isLoading && !selectedItem) {
    return <PageLoadingView />;
  }

  return (
    <div className="w-full h-screen flex flex-col text-gray-800 dark:text-white overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Special Products</h1>
        <button
          onClick={handleAddItem}
          className="bg-blue-600 text-white py-1 px-4 rounded hover:bg-blue-500"
        >
          + Add Product
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6">
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            if (q === "") {
              setSearchTerm("");
            }
          }}
          onSearchSubmit={() => {
            setIsSearching(true);
            setSearchTerm(searchQuery);
            setTimeout(() => setIsSearching(false), 100);
          }}
          isLoading={isSearching}
          placeholder="Search for a product"
        />
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-6">
        {error ? (
          <PageErrorView
            title="Error loading special products"
            onRetry={refresh}
          />
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery
              ? "No products match your search"
              : "No products found"}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredItems.map((item) => (
              <div
                key={item.productId}
                className="py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => handleItemSelect(item)}
              >
                <div className="flex-1">
                  <h3 className="text-lg font-medium">{item.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    ${item.amount}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {item.description}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.productId);
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Show
            </span>
            <select
              value={pagination.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 pr-8"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              items per page
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {pagination.page} of{" "}
              {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={
                pagination.page ===
                Math.ceil(pagination.total / pagination.limit)
              }
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <DataManagementSidebar
        title={isCreating ? "Add Special Product" : "Edit Special Product"}
        selectedItem={selectedItem}
        onClose={() => {
          setSelectedItem(null);
          setIsCreating(false);
          setValidationErrors({});
          setUpdateError(null);
        }}
        onSave={handleSave}
        isUpdating={isUpdating}
        updateError={updateError}
      >
        <div>
          <label className="block text-sm font-medium mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={selectedItem?.name || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, name: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.name ? "border-red-500" : ""
            }`}
          />
          {validationErrors.name && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={selectedItem?.description || ""}
            onChange={(e) =>
              setSelectedItem((prev) =>
                prev ? { ...prev, description: e.target.value } : null
              )
            }
            className={`w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 ${
              validationErrors.description ? "border-red-500" : ""
            }`}
          />
          {validationErrors.description && (
            <p className="mt-1 text-sm text-red-500">
              {validationErrors.description}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Price <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
              USD
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={selectedItem?.amount || 0}
              onChange={(e) =>
                setSelectedItem((prev) =>
                  prev
                    ? { ...prev, amount: parseFloat(e.target.value) || 0 }
                    : null
                )
              }
              className={`flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 ${
                validationErrors.amount ? "border-red-500" : ""
              }`}
            />
          </div>
          {validationErrors.amount && (
            <p className="mt-1 text-sm text-red-500">
              {validationErrors.amount}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Product Image
          </label>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Image uploads aren't yet available for special products.
            </p>
          </div>
        </div>
      </DataManagementSidebar>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen: false }))
        }
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        cancelText={confirmationDialog.cancelText}
      />
    </div>
  );
};

export default SpecialProductsData;
